import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  RefreshCw,
  Search,
  Star,
  Tag,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ServiceBanner } from '@/components/ServiceBanner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useBanner } from '@/hooks/useBanner'
import { trackEvent } from '@/lib/analytics'

interface EventItem {
  title: string
  description: string
  url: string
  imageUrl?: string
  eventDate: string
  eventTime: string
  location: string
  category: string
  registrationWarning?: string
  registrationClosed?: boolean
  priceRange?: string
}

interface EventsScreenProps {
  onBack: () => void
  partyId?: string
  initialTab?: string
}

const PAGE_SIZE = 10
const CACHE_KEY = 'reinz_events'
const CACHE_TTL = 30 * 60 * 1000

function getCached<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const { data, fetchedAt, ttl } = JSON.parse(raw)
    if (Date.now() - fetchedAt >= ttl) return null
    return data as T
  } catch {
    return null
  }
}

function setCached<T>(key: string, data: T, ttl: number): void {
  try {
    localStorage.setItem(key, JSON.stringify({ data, fetchedAt: Date.now(), ttl }))
  } catch {}
}

export function EventsScreen({ onBack, partyId, initialTab }: EventsScreenProps) {
  const banner = useBanner('events')
  const [events, setEvents] = useState<EventItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedYear, setSelectedYear] = useState('All')
  const [sortBy, setSortBy] = useState<'date' | 'title'>('date')
  const [page, setPage] = useState(1)
  const [savedUrls, setSavedUrls] = useState<Set<string>>(new Set())
  const [savedEvents, setSavedEvents] = useState<EventItem[]>([])
  const [registeredTitles, setRegisteredTitles] = useState<Set<string>>(new Set())
  const listRef = useRef<HTMLDivElement>(null)

  const loadEvents = (bust = false) => {
    if (!bust) {
      const cached = getCached<EventItem[]>(CACHE_KEY)
      if (cached) {
        setEvents(cached)
        setLoading(false)
        return
      }
    }
    setLoading(true)
    localStorage.removeItem(CACHE_KEY)
    fetch('api/events/listing')
      .then((r) => r.json())
      .then((data) => {
        setEvents(data)
        setCached(CACHE_KEY, data, CACHE_TTL)
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadEvents()
  }, [])
  useEffect(() => {
    trackEvent('screen_viewed', { screen: 'events' })
  }, [])

  useEffect(() => {
    if (!partyId) return
    fetch(`api/user-saved-items?userId=${encodeURIComponent(partyId)}&itemType=event`)
      .then((r) => r.json())
      .then((items: { itemData: string }[]) => {
        const parsed = items.map((i) => JSON.parse(i.itemData) as EventItem)
        setSavedEvents(parsed)
        setSavedUrls(new Set(parsed.map((e) => e.url)))
      })
      .catch(() => {})
  }, [partyId])

  const fetchRegistrations = (id: string) => {
    if (!id) return
    fetch(`api/events/registrations?partyId=${id}`)
      .then((r) => r.json())
      .then((keys: string[]) => setRegisteredTitles(new Set(keys.map((k) => k.toLowerCase()))))
      .catch(() => {})
  }

  useEffect(() => {
    fetchRegistrations(partyId ?? '')
  }, [partyId])

  const handleToggleSave = async (event: EventItem) => {
    if (!partyId) return
    const isSaved = savedUrls.has(event.url)
    try {
      if (isSaved) {
        const r = await fetch(
          `api/user-saved-items?userId=${encodeURIComponent(partyId)}&itemType=event&itemId=${encodeURIComponent(event.url)}`,
          { method: 'DELETE' },
        )
        if (r.ok) {
          setSavedUrls((prev) => {
            const s = new Set(prev)
            s.delete(event.url)
            return s
          })
          setSavedEvents((prev) => prev.filter((e) => e.url !== event.url))
        }
      } else {
        const r = await fetch('api/user-saved-items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: partyId,
            itemType: 'event',
            itemId: event.url,
            itemData: JSON.stringify(event),
          }),
        })
        if (r.ok) {
          setSavedUrls((prev) => new Set([...prev, event.url]))
          trackEvent('item_saved', { itemType: 'event', itemId: event.url })
          setSavedEvents((prev) => [event, ...prev])
        }
      }
    } catch {
      /* network error — leave UI state unchanged */
    }
  }

  const categories = useMemo(() => {
    const unique = Array.from(new Set(events.map((e) => e.category).filter(Boolean)))
    return ['All', ...unique.sort()]
  }, [events])

  const years = useMemo(() => {
    const unique = Array.from(
      new Set(events.map((e) => e.eventDate?.split(' ')[2]).filter(Boolean)),
    ) as string[]
    return ['All', ...unique.sort().reverse()]
  }, [events])

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return events.filter((e) => {
      const matchesCategory = selectedCategory === 'All' || e.category === selectedCategory
      const matchesYear = selectedYear === 'All' || e.eventDate?.includes(selectedYear)
      const matchesSearch =
        !q ||
        e.title.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q)
      return matchesCategory && matchesYear && matchesSearch
    })
  }, [events, search, selectedCategory, selectedYear])

  const sorted = useMemo(() => {
    if (sortBy === 'title') return [...filtered].sort((a, b) => a.title.localeCompare(b.title))
    return filtered
  }, [filtered, sortBy])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const paginated = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }
  const handleCategory = (cat: string) => {
    setSelectedCategory(cat)
    setPage(1)
  }
  const handlePageChange = (p: number) => {
    setPage(p)
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const renderCard = (event: EventItem, i: number) => {
    const isSaved = savedUrls.has(event.url)
    const eventKey = event.url?.match(/EventKey=([^&]+)/i)?.[1]?.toLowerCase() ?? ''
    const isRegistered = eventKey ? registeredTitles.has(eventKey) : false
    return (
      <div key={i} className="rounded-xl overflow-hidden border border-border bg-card">
        {/* Image or fallback */}
        {event.imageUrl ? (
          <div className="relative h-44 overflow-hidden">
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-full h-full object-cover object-[center_20%]"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent" />
            {event.eventDate && (
              <div className="absolute bottom-2 left-3 bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded-md">
                {event.eventDate}
              </div>
            )}
            <button
              type="button"
              onClick={() => handleToggleSave(event)}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            >
              <Star
                className={`w-4 h-4 ${isSaved ? 'fill-yellow-400 text-yellow-400' : 'text-white'}`}
              />
            </button>
          </div>
        ) : (
          <div className="relative h-20 bg-linear-to-r from-primary to-primary/70 flex items-center justify-center">
            <CalendarDays className="w-8 h-8 text-primary-foreground/40" />
            {event.eventDate && (
              <div className="absolute bottom-2 left-3 bg-white/20 text-white text-xs font-bold px-2 py-1 rounded-md">
                {event.eventDate}
              </div>
            )}
            <button
              type="button"
              onClick={() => handleToggleSave(event)}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            >
              <Star
                className={`w-4 h-4 ${isSaved ? 'fill-yellow-400 text-yellow-400' : 'text-white'}`}
              />
            </button>
          </div>
        )}

        <div className="p-3">
          {event.registrationWarning && (
            <div
              className={`flex items-center gap-1 text-3xs font-medium rounded px-2 py-1 mb-2 ${event.registrationClosed ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}
            >
              <AlertCircle className="w-3 h-3 shrink-0" />
              {event.registrationWarning}
            </div>
          )}
          {event.category && (
            <span className="inline-flex items-center gap-1 text-3xs font-medium text-primary mb-1">
              <Tag className="w-3 h-3" />
              {event.category}
            </span>
          )}
          <p className="text-sm font-semibold text-foreground line-clamp-2 mb-2">{event.title}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2">
            {event.eventTime && (
              <span className="flex items-center gap-1 text-3xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {event.eventTime}
              </span>
            )}
            {event.location && (
              <span className="flex items-center gap-1 text-3xs text-muted-foreground min-w-0">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">{event.location}</span>
              </span>
            )}
            {event.priceRange && (
              <span className="text-3xs text-muted-foreground">{event.priceRange}</span>
            )}
          </div>
          {event.description && (
            <p className="text-3xs text-muted-foreground mb-3 line-clamp-2">{event.description}</p>
          )}
          {event.url &&
            (isRegistered ? (
              <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-medium px-3 py-1.5 rounded-lg">
                ✓ Registered
              </span>
            ) : event.registrationClosed ? (
              <span className="inline-flex items-center gap-1 bg-muted text-muted-foreground text-xs font-medium px-3 py-1.5 rounded-lg cursor-not-allowed">
                Registration Closed
              </span>
            ) : (
              <a
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('register_clicked', { event: event.title })}
                className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Register
                <ChevronRight className="w-3 h-3" />
              </a>
            ))}
        </div>
      </div>
    )
  }

  return (
    <>
      <ServiceBanner banner={banner} />
      <Tabs defaultValue={initialTab ?? 'all'}>
        {/* Sticky header */}
        <div className="sticky top-0 z-30 bg-background px-4 pb-2">
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-foreground flex-1 font-heading">
              Upcoming Events
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => loadEvents(true)}
              disabled={loading}
              className="h-8 w-8"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <TabsList className="w-full grid grid-cols-2 mb-0">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              All Events
            </TabsTrigger>
            <TabsTrigger value="saved" className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Saved {savedEvents.length > 0 && `(${savedEvents.length})`}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="px-4 pb-20">
          {/* All Events Tab */}
          <TabsContent value="all">
            {/* Search + Sort + Year */}
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search events..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9 text-sm"
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value as 'date' | 'title')
                  setPage(1)
                }}
                className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
              >
                <option value="date">Date</option>
                <option value="title">Title A–Z</option>
              </select>
              {years.length > 2 && (
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(e.target.value)
                    setPage(1)
                  }}
                  className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Category pills */}
            {!loading && categories.length > 2 && (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-3 scrollbar-hide">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleCategory(cat)}
                    className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedCategory === cat
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Results count */}
            {!loading && sorted.length > 0 && (
              <p className="text-xs text-muted-foreground mb-3">
                {`Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, sorted.length)} of ${sorted.length} event${sorted.length !== 1 ? 's' : ''}`}
              </p>
            )}

            {/* Skeletons */}
            {loading && (
              <div className="flex flex-col gap-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="rounded-xl bg-muted animate-pulse h-28" />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && sorted.length === 0 && (
              <div className="flex flex-col items-center justify-center mt-10 gap-3 text-center">
                <CalendarDays className="w-10 h-10 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">
                  No events found{search ? ` for "${search}"` : ''}
                </p>
                {search && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSearch('')}
                    className="text-xs"
                  >
                    Clear search
                  </Button>
                )}
              </div>
            )}

            {/* Event cards */}
            {!loading && paginated.length > 0 && (
              <div ref={listRef} className="flex flex-col gap-3">
                {paginated.map((event, i) => renderCard(event, i))}
              </div>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 text-xs"
                >
                  <ChevronLeft className="w-3 h-3" /> Previous
                </Button>
                <span className="text-xs text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 text-xs"
                >
                  Next <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Saved Events Tab */}
          <TabsContent value="saved">
            {savedEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center mt-10 gap-3 text-center">
                <Star className="w-10 h-10 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">No saved events yet</p>
                <p className="text-xs text-muted-foreground">
                  Tap the ★ on any event to save it here
                </p>
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-border rounded-xl border border-border overflow-hidden">
                {savedEvents.map((event, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 bg-card hover:bg-muted/30 transition-colors"
                  >
                    {/* Date badge */}
                    <div className="shrink-0 w-12 text-center bg-primary/10 rounded-lg p-1.5">
                      <p className="text-xs font-bold text-primary leading-tight">
                        {event.eventDate?.split(' ')[0] ?? '—'}
                      </p>
                      <p className="text-3xs text-primary/70 uppercase leading-tight">
                        {event.eventDate?.split(' ')[1] ?? ''}
                      </p>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground line-clamp-1">
                        {event.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {event.eventTime && (
                          <span className="flex items-center gap-1 text-3xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {event.eventTime}
                          </span>
                        )}
                        {event.location && (
                          <span className="flex items-center gap-1 text-3xs text-muted-foreground min-w-0">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{event.location}</span>
                          </span>
                        )}
                      </div>
                      {event.url &&
                        ((event.url?.match(/EventKey=([^&]+)/i)?.[1]?.toLowerCase() ?? '') !== '' &&
                        registeredTitles.has(
                          event.url?.match(/EventKey=([^&]+)/i)?.[1]?.toLowerCase() ?? '',
                        ) ? (
                          <span className="text-3xs text-green-700 font-medium mt-0.5 inline-block">
                            ✓ Registered
                          </span>
                        ) : (
                          <a
                            href={event.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackEvent('register_clicked', { event: event.title })}
                            className="text-3xs text-primary hover:underline mt-0.5 inline-block"
                          >
                            Register →
                          </a>
                        ))}
                    </div>

                    {/* Unsave */}
                    <button
                      type="button"
                      onClick={() => handleToggleSave(event)}
                      className="shrink-0 p-1.5 rounded-full hover:bg-muted transition-colors"
                      title="Remove from saved"
                    >
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </>
  )
}
